import { useCallback, useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import {
  useButtonPressedCallback,
  useModalDisplayedEvent
} from '@zenbusiness/application-commons-clickstream';
import {
  FormattedMessage,
  useIntl
} from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import CardHeader from '@zenbusiness/zui/CardHeader';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';

import { useCarouselContext } from '#client/components/Carousel/CarouselContext';

import {
  hasError, list, match, required
} from './formValidation';
import * as Styled from './OptInContent.styles';
import DisclaimerModal from '../DisclaimerModal/DisclaimerModal';

export const LOCALIZED_TEXT = {
  title: 'dashboard.carousel.card.smsOptIn.form.title',
  subtitle: 'dashboard.carousel.card.smsOptIn.form.subTitle',
  label: 'dashboard.carousel.card.smsOptIn.form.label',
  phoneNumberLabel: 'dashboard.carousel.card.smsOptIn.form.phoneNumber.label',
  phoneNumberRequired:
    'dashboard.carousel.card.smsOptIn.form.phoneNumber.required',
  phoneNumberValidation:
    'dashboard.carousel.card.smsOptIn.form.phoneNumber.validation',
  submit: 'dashboard.carousel.card.smsOptIn.form.button.submit',
  disclaimer: 'dashboard.carousel.card.smsOptIn.form.disclaimer.text',
  disclaimerLink: 'dashboard.carousel.card.smsOptIn.form.disclaimer.link'
};

function formatPhoneNumber(phoneNumber) {
  let formattedPhoneNumber = phoneNumber.replace(/\D/g, '');
  formattedPhoneNumber = formattedPhoneNumber.substring(0, 10);
  if (formattedPhoneNumber.length > 6) {
    return formattedPhoneNumber.replace(/^(.{3})(.{3})/, '$1-$2-');
  }

  if (formattedPhoneNumber.length > 3) {
    return formattedPhoneNumber.replace(/^(.{3})/, '$1-');
  }

  return formattedPhoneNumber;
}

const validators = {
  phoneNumber: list(
    required(LOCALIZED_TEXT.phoneNumberRequired),
    match(/^[0-9]{3}-[0-9]{3}-[0-9]{4}$/, LOCALIZED_TEXT.phoneNumberValidation)
  )
};

const SMS_OPT_IN_STATUS = true;

function OptInContent({
  onOptIn = () => {},
  currentPhoneNumber = '',
  saving = false
}) {
  const { formatMessage: fm } = useIntl();
  const { stopAutoplay } = useCarouselContext();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  useEffect(() => {
    if (currentPhoneNumber && currentPhoneNumber !== '') {
      setPhoneNumber(formatPhoneNumber(currentPhoneNumber));
    }
  }, [currentPhoneNumber]);

  const trackOptInButtonPressed = useButtonPressedCallback({
    appSection: 'home',
    elementName: 'opt_in_button'
  });

  useModalDisplayedEvent({
    open: !!showDisclaimerModal,
    elementName: 'opt_in_modal'
  });

  const toggleViewDisclaimer = useCallback(() => {
    setShowDisclaimerModal((v) => !v);
  }, []);

  const handleOnChange = useCallback(({ target }) => {
    setPhoneNumber(formatPhoneNumber(target.value));
  }, []);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();
      const validationResult = validators.phoneNumber(phoneNumber);
      if (validationResult === undefined) {
        trackOptInButtonPressed();
        onOptIn(phoneNumber, SMS_OPT_IN_STATUS);
      } else {
        setValidationErrors({
          phoneNumber: validationResult
        });
      }
    },
    [onOptIn, phoneNumber, trackOptInButtonPressed]
  );

  const handleBlur = useCallback(
    ({ target }) => {
      const result = validators[target.name](target.value);
      setValidationErrors({
        ...validationErrors,
        [target.name]: result
      });
    },
    [validationErrors]
  );

  const renderError = useCallback(
    (fieldName) => {
      const errorMessageId = hasError(validationErrors, fieldName)
        ? validationErrors[fieldName]
        : null;

      return errorMessageId ? <FormattedMessage id={errorMessageId} /> : '';
    },
    [validationErrors]
  );

  return (
    <>
      <Styled.CardContent spacing={2}>
        <CardHeader
          subtitle={fm({ id: LOCALIZED_TEXT.subtitle })}
          title={fm({ id: LOCALIZED_TEXT.title })}
        />
        <Stack spacing={1}>
          <Box
            aria-label={fm({ id: LOCALIZED_TEXT.label })}
            autoComplete="off"
            component="form"
            noValidate
            onSubmit={handleSubmit}
          >
            <Stack spacing={1}>
              <Text
                component="label"
                color="tertiary"
                htmlFor="phoneNumber"
                size="xs"
                weight="regular"
              >
                <FormattedMessage id={LOCALIZED_TEXT.phoneNumberLabel} />
              </Text>
              <Styled.InputContainer>
                <TextField
                  error={hasError(validationErrors, 'phoneNumber')}
                  fullWidth
                  helperText={renderError('phoneNumber')}
                  id="phoneNumber"
                  name="phoneNumber"
                  onBlur={handleBlur}
                  onChange={handleOnChange}
                  onFocus={stopAutoplay}
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  placeholder="999-999-9999"
                  required
                  size="sm"
                  type="tel"
                  value={phoneNumber}
                />
                <Styled.OptInButton loading={saving} size="sm" type="submit">
                  <FormattedMessage id={LOCALIZED_TEXT.submit} />
                </Styled.OptInButton>
              </Styled.InputContainer>
            </Stack>
          </Box>
          <Text color="tertiary" size="xs" weight="regular">
            <FormattedMessage id={LOCALIZED_TEXT.disclaimer} />
            &nbsp;
            <Styled.Link
              component="span"
              onClick={toggleViewDisclaimer}
              role="button"
            >
              <FormattedMessage id={LOCALIZED_TEXT.disclaimerLink} />
            </Styled.Link>
            .
          </Text>
        </Stack>
      </Styled.CardContent>
      {showDisclaimerModal && (
        <DisclaimerModal onClose={toggleViewDisclaimer} />
      )}
    </>
  );
}

OptInContent.propTypes = {
  saving: PropTypes.bool,
  onOptIn: PropTypes.func,
  currentPhoneNumber: PropTypes.string
};

export default OptInContent;
